import { Link } from "react-router-dom";
import { ClipboardList } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, ReferenceLine } from "recharts";
import { useAuth, levelOf } from "../../context/AuthContext";
import { useLevelFilter } from "../../hooks/useLevelFilter";
import { useApi } from "../../hooks/useApi";
import { toList } from "../../lib/api";
import { PageHeader, Panel, StatTile, Loader, ErrorNote, Empty, shortDate, fmt } from "../../components/app/kit";
import { TeamLeaders } from "../../components/app/TeamLeaders";
import { TeamStandards } from "../../components/app/TeamStandards";

export const scoreOf = (g) => ({
  us: g?.us ?? g?.final_score_us ?? g?.our_score ?? null,
  them: g?.them ?? g?.final_score_them ?? g?.opponent_score ?? null,
});
export const resultOf = (g) => {
  if (g?.result) return String(g.result).toUpperCase().charAt(0);
  const { us, them } = scoreOf(g);
  return us == null || them == null ? "—" : us > them ? "W" : us < them ? "L" : "T";
};
export const eventsOf = (d) => [...toList(d, ["events", "items"]), ...toList(d?.upcoming), ...toList(d?.past)];
export const eventTitle = (e) => e?.title ?? (e?.opponent ? `vs ${e.opponent}` : e?.name ?? e?.type ?? "Event");
export const eventDate = (e) => e?.date ?? e?.start ?? e?.starts_at ?? e?.datetime ?? e?.start_time;

const Trend = ({ games }) => {
  const data = games.map((g, i) => {
    const { us, them } = scoreOf(g);
    return { name: g.opponent ?? `G${i + 1}`, margin: (us ?? 0) - (them ?? 0), us, them, r: resultOf(g) };
  });
  return (
    <div className="h-64 w-full" data-testid="trend-chart">
      <ResponsiveContainer>
        <BarChart data={data}>
          <XAxis dataKey="name" stroke="#94A3B8" fontSize={11} tickLine={false} />
          <YAxis stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} width={30} />
          <ReferenceLine y={0} stroke="#334155" />
          <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid #1E293B", borderRadius: 12 }} formatter={(v, n, p) => [`${p.payload.us ?? "—"}–${p.payload.them ?? "—"} (${v > 0 ? "+" : ""}${v})`, "Score"]} />
          <Bar dataKey="margin" radius={[4, 4, 4, 4]}>
            {data.map((d, i) => <Cell key={i} fill={d.margin >= 0 ? "#39FF14" : "#FF3B30"} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default function Overview() {
  const { membership, level, canEdit } = useAuth();
  const tid = membership?.team_id;
  const lf = useLevelFilter(tid);
  const own = useApi(tid && level === "all" && !canEdit && membership?.player_id && `/teams/${tid}/players/${membership.player_id}`);
  const standardsLevel = level !== "all" ? level : own.data ? levelOf(own.data) : null;
  const team = useApi(tid && `/teams/${tid}`);
  const trend = useApi(tid && `/teams/${tid}/trend`);
  const stats = useApi(tid && `/teams/${tid}/stats/team`);
  const sched = useApi(tid && `/teams/${tid}/schedule`);

  if (!membership) return <Empty title="No team yet" text="This account isn't on a team. Ask your coach for an invite in the GAMEDAY app." />;
  const games = toList(trend.data, ["games", "items"]);
  const w = games.filter((g) => resultOf(g) === "W").length;
  const l = games.filter((g) => resultOf(g) === "L").length;
  const now = Date.now();
  const upcoming = eventsOf(sched.data).filter((e) => new Date(eventDate(e)).getTime() >= now - 864e5).slice(0, 4);

  return (
    <div>
      <PageHeader eyebrow={`${membership.web_role ?? membership.role} · ${team.data?.subscription_status ?? membership.subscription_status ?? "team"}`} title={team.data?.name ?? membership.team_name}>
        <Link to="/coach/roster" data-testid="overview-roster-link" className="rounded-full border border-[#1E293B] px-5 py-2.5 text-sm font-semibold text-white hover:border-[#39FF14]">View roster</Link>
        {canEdit && <Link to="/coach/enter-stats" data-testid="overview-enter-stats" className="inline-flex items-center gap-2 rounded-full bg-[#39FF14] px-5 py-2.5 text-sm font-semibold text-[#0A0E1A] hover:bg-[#30D911]"><ClipboardList className="h-4 w-4" />Enter stats</Link>}
      </PageHeader>
      {team.error && <ErrorNote error={team.error} />}
      <TeamStandards tid={tid} level={standardsLevel} canEdit={canEdit} />
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile testid="overview-wins" label="Wins" value={w} accent />
        <StatTile testid="overview-losses" label="Losses" value={l} />
        <StatTile label="Games" value={games.length} />
        <StatTile label="Win %" value={games.length ? `${fmt((100 * w) / games.length, 0)}%` : "—"} />
      </div>
      <div className="grid gap-6 xl:grid-cols-3">
        <Panel className="xl:col-span-2">
          <h2 className="mb-1 font-display text-2xl font-bold uppercase text-white">Program trajectory</h2>
          <p className="mb-4 text-sm text-slate-400">Point margin, game by game.</p>
          {trend.loading ? <Loader /> : trend.error ? <ErrorNote error={trend.error} /> : games.length ? <Trend games={games} /> : <Empty title="No games yet" />}
        </Panel>
        <Panel>
          <h2 className="mb-4 font-display text-2xl font-bold uppercase text-white">Up next</h2>
          {sched.loading ? <Loader /> : upcoming.length ? (
            <ul className="space-y-3">
              {upcoming.map((e, i) => (
                <li key={e.id ?? i} data-testid="upcoming-event" className="rounded-xl bg-[#162032] px-4 py-3">
                  <p className="font-semibold text-white">{eventTitle(e)}</p>
                  <p className="font-mono-gd text-xs uppercase tracking-wider text-slate-400">{shortDate(eventDate(e))}{e.location ? ` · ${e.location}` : ""}</p>
                </li>
              ))}
            </ul>
          ) : <Empty title="Nothing scheduled" />}
        </Panel>
      </div>
      <Panel className="mt-6">
        <h2 className="mb-4 font-display text-2xl font-bold uppercase text-white">Team leaders</h2>
        {stats.loading ? <Loader /> : stats.error ? <ErrorNote error={stats.error} /> : <TeamLeaders rows={toList(stats.data, ["players"]).filter((r) => lf.keep(r.player_id ?? r.id))} />}
      </Panel>
    </div>
  );
}
